import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PLANS } from "@/lib/data";
import { MaskedLines } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const Pricing = () => {
  return (
    <section id="pricing" className="relative bg-[#FAFAF8] py-24 lg:py-40" data-testid="pricing-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <p className="eyebrow text-[#C89933] mb-4">Transparent Fees</p>
          <h2 className="font-serif-display text-[#0F1E3D] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight">
            <MaskedLines lines={["No commissions.", "No surprises."]} />
          </h2>
          <p className="mt-6 max-w-xl text-[#4A5568] text-base lg:text-lg leading-relaxed font-light">
            In an industry built on hidden commissions, we publish our fees openly. You always know exactly what you pay, and exactly what you get in return.
          </p>
        </div>

        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
              className={`relative flex flex-col p-8 lg:p-10 ${plan.featured ? "bg-[#0F1E3D]" : "bg-[#FAFAF8]"}`}
              data-testid={`pricing-plan-${plan.id}`}
            >
              <div className="flex items-start justify-between gap-3 min-h-[26px]">
                <p className={`overline text-[0.62rem] ${plan.featured ? "text-[#C89933]" : "text-[#7F8D76]"}`}>
                  {plan.concept}
                </p>
                {plan.featured && (
                  <span className="shrink-0 rounded-full bg-[#C89933] px-3 py-1 text-[0.5rem] font-semibold uppercase tracking-[0.16em] text-[#0F1E3D]">
                    Most Chosen
                  </span>
                )}
              </div>
              <h3 className={`mt-5 font-serif-display text-3xl lg:text-4xl tracking-tight ${plan.featured ? "text-[#FAFAF8]" : "text-[#0F1E3D]"}`}>
                {plan.name}
              </h3>
              <p className={`mt-3 text-sm font-light ${plan.featured ? "text-[#FAFAF8]/60" : "text-[#4A5568]"}`}>
                {plan.idealFor}
              </p>

              <div className={`mt-8 pt-8 border-t ${plan.featured ? "border-white/10" : "border-[#E2E8F0]"}`}>
                <div className="flex items-baseline gap-2">
                  <span className={`font-serif-display text-3xl lg:text-4xl tracking-tight ${plan.featured ? "text-[#FAFAF8]" : "text-[#0F1E3D]"}`}>
                    {plan.fee}
                  </span>
                </div>
                <p className={`mt-1 text-sm font-light ${plan.featured ? "text-[#FAFAF8]/50" : "text-[#4A5568]"}`}>
                  {plan.feeNote}
                </p>
                <p className={`mt-4 overline text-[0.58rem] ${plan.featured ? "text-[#FAFAF8]/70" : "text-[#7F8D76]"}`}>
                  Max Intake · {plan.maxIntake} clients
                </p>
              </div>

              <ul className="mt-8 space-y-4 flex-1">
                {plan.inclusions.map((inc) => (
                  <li key={inc} className="flex items-start gap-3">
                    <Check className={`h-4 w-4 mt-1 shrink-0 ${plan.featured ? "text-[#C89933]" : "text-[#7F8D76]"}`} strokeWidth={2} />
                    <span className={`text-sm leading-relaxed ${plan.featured ? "text-[#FAFAF8]/80" : "text-[#4A5568]"}`}>
                      {inc}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => scrollToSection("contact")}
                className={`mt-10 w-full rounded-full py-3.5 text-sm font-medium transition-colors duration-400 ${
                  plan.featured
                    ? "bg-[#C89933] text-[#0F1E3D] hover:bg-[#FAFAF8]"
                    : "bg-[#0F1E3D] text-[#FAFAF8] hover:bg-[#C89933] hover:text-[#0F1E3D]"
                }`}
                data-testid={`pricing-cta-${plan.id}`}
              >
                Enquire
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-xs text-[#4A5568]/70 font-light max-w-2xl">
          All fees are fixed and disclosed upfront under a fee-only fiduciary standard. The Generational plan is charged at 1% of assets under advisory above ₹1.5 crore.
        </p>
      </div>
    </section>
  );
};

export default Pricing;
